import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

const BASE = process.env.REACT_APP_API_URL ?? "http://localhost:5000/api";

interface Hackathon {
  _id: string;
  title: string;
  organizerName: string;
  startDate: string;
  endDate: string;
  mode: string;
  status?: string;
}

const computeStatus = (startDate: string, endDate: string) => {
  const now = new Date();
  if (now < new Date(startDate)) return "Upcoming";
  if (now <= new Date(endDate)) return "Active";
  return "Completed";
};

const statusColors: Record<string, string> = {
  Upcoming: "bg-yellow-100 text-yellow-700",
  Active: "bg-blue-100 text-blue-700",
  Completed: "bg-green-100 text-green-700",
};

const fmt = (d: string) =>
  new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

export function HackathonList() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [hackathons, setHackathons] = useState<Hackathon[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    fetch(`${BASE}/hackathon-events`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => setHackathons(data.items ?? data ?? []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <div className="px-3 py-8">
      <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#1a2332]">Manage Hackathons</h1>
          <p className="text-sm text-[#6e7891] mt-1">View and manage all hackathon events.</p>
        </div>
        <button
          onClick={() => navigate("/admin-dashboard/manage-hackathon/create")}
          className="mt-4 md:mt-0 w-full md:w-auto px-5 py-2.5 bg-[#0058ab] text-white rounded text-sm font-medium hover:bg-[#004a94] transition-colors"
        >
          + Create Hackathon
        </button>
      </div>

      {loading ? (
        <p className="text-gray-400 text-sm">Loading hackathons...</p>
      ) : hackathons.length === 0 ? (
        <p className="text-gray-400 text-sm">No hackathons found. Create one to get started.</p>
      ) : (
        <div className="flex flex-col gap-4">
          {(() => {
            const statusOrder = { Active: 0, Upcoming: 1, Completed: 2 };
            const sorted = [...hackathons].sort((a, b) => {
              const statusA = computeStatus(a.startDate, a.endDate);
              const statusB = computeStatus(b.startDate, b.endDate);
              return (statusOrder[statusA as keyof typeof statusOrder] ?? 99) - (statusOrder[statusB as keyof typeof statusOrder] ?? 99);
            });
            return sorted.map(h => {
              const status = computeStatus(h.startDate, h.endDate);
              return (
                <div key={h._id} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 flex flex-col md:flex-row md:items-center md:justify-between">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3 mb-1">
                      <h3 className="text-base font-semibold text-[#1a2332]">{h.title}</h3>
                      <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${statusColors[status]}`}>
                        {status}
                      </span>
                      <span className="text-xs px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-600 capitalize">{h.mode}</span>
                    </div>
                    <p className="text-sm text-[#6e7891]">
                      {h.organizerName} &nbsp;·&nbsp; {fmt(h.startDate)} – {fmt(h.endDate)}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {status !== "Completed" && (
                      <button
                        onClick={() => navigate(`/admin-dashboard/manage-hackathon/${h._id}`)}
                        className="px-4 py-2 bg-[#0058ab] text-white rounded-lg text-sm font-medium hover:bg-[#004a94] transition-colors"
                      >
                        Edit
                      </button>
                    )}
                  </div>
                </div>
              );
            });
          })()}
        </div>
      )}
    </div>
  );
}
